import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ShopSetup from '../components/shop/ShopSetup';
import BottomNav from '../components/BottomNav';
import Header from '../components/Header';
import BackButton from '../components/BackButton';
import AuthChoiceModal from '../components/AuthChoiceModal';
import { toast } from 'sonner';

/**
 * Standalone professional signup page.
 *
 * Behaviour:
 *  - Renders ShopSetup in `signup-pro` mode (atomic email+password+shop creation).
 *  - If the user is already authenticated WITH a shop, redirect to /dashboard.
 *  - If authenticated WITHOUT a shop, fall back to the regular /negoziante flow.
 *  - If ShopSetup surfaces the "email already registered" error, we expose a
 *    "Effettua il login" CTA that opens the AuthChoiceModal inline; after
 *    login the user lands either on /dashboard (has shop) or /negoziante
 *    (needs to complete the shop) — no re-typing needed.
 */
export default function RegistrazioneAttivitaPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, setUser, loading: authLoading } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated && user?.has_shop) {
      navigate('/dashboard');
    } else if (isAuthenticated) {
      // Already a customer account → use the legacy shop-only flow.
      navigate('/negoziante');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  const handleCreated = (proUser) => {
    setUser(proUser);
    toast.success('Account creato! Benvenuto su Kyloo.');
    navigate('/dashboard');
  };

  const handleLoginSuccess = () => {
    // The useEffect above will forward the freshly-logged-in user to the
    // right destination as soon as AuthContext updates.
    setLoginOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="registrazione-attivita-page">
      <Header showSearch={false} />
      <main className="max-w-6xl mx-auto px-4 py-4">
        <BackButton fallback="/" className="mb-3" />
        <h1 className="font-outfit font-bold text-2xl sm:text-3xl text-slate-800 mb-4">
          Registrazione attività
        </h1>
        <ShopSetup
          mode="signup-pro"
          onShopCreated={handleCreated}
          onLoginRequested={() => setLoginOpen(true)}
        />
      </main>

      <BottomNav />

      <AuthChoiceModal
        open={loginOpen}
        onOpenChange={setLoginOpen}
        intent="professional"
        initialMode="login"
        onSuccess={handleLoginSuccess}
      />
    </div>
  );
}
